import type { InputBasePropsSizeOverrides } from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import type { TextFieldVariants } from '@mui/material/TextField';
import type { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import { OverridableStringUnion } from '@mui/types';
import { isMobile } from 'react-device-detect';

import { PropWithTestId, Sxable } from 'library/types';
import { Modify } from 'library/utils/typescript';

import { SelectOption, SelectProps } from './SelectCommon';

export const SmallSx = (theme: Theme) => ({
	flex: 1,
	label: {
		color: theme.palette.secondary.main,
		fontSize: theme.typography.subtitle2.fontSize,
		top: `-${theme.spacing(2.5)}`,
	},
	'>div': {
		maxHeight: theme.spacing(2.5),
		marginTop: '0 !important', // to override default style to present a select having max height
	},
});

export const FocusedLabelSx: SystemStyleObject = {
	label: {
		'&.Mui-focused': {
			display: 'none',
		},
	},
};

type ModifiedSelectProps = Modify<
	SelectProps<SelectOption>,
	{
		onChange: (option: SelectOption) => void;
		selectedOption: string | number;
	}
>;

export interface UncontrolledSelectProps
	extends ModifiedSelectProps,
		Sxable,
		PropWithTestId {
	required?: boolean;
	disabled?: boolean;
	size?: OverridableStringUnion<
		'small' | 'medium',
		InputBasePropsSizeOverrides
	>;
	error?: string | boolean;
	variant?: TextFieldVariants;
	loading?: boolean;
}

const NativeOption = ({
	children,
	...props
}: React.DetailedHTMLProps<
	React.OptionHTMLAttributes<HTMLOptionElement>,
	HTMLOptionElement
>) => <option {...props}>{children}</option>;

const NativeLabelOptionValue = 'NativeLabelOptionValue';

export const UncontrolledSelect = ({
	id,
	className,
	label,
	onChange,
	options,
	selectedOption,
	required = false,
	dataTestId,
	disabled = false,
	size = 'medium',
	error = undefined,
	sx = [],
	variant = 'outlined',
	loading = false,
}: UncontrolledSelectProps) => {
	if (loading) {
		return <Skeleton variant="rounded" animation="wave" height="2.5rem" />;
	}

	const Option = isMobile ? NativeOption : MenuItem;
	const NativeLabelOption = (
		<NativeOption
			disabled
			selected
			key={NativeLabelOptionValue}
			value={NativeLabelOptionValue}>
			{`${label}${required ? ' *' : ''}`}
		</NativeOption>
	);

	const handleChange: React.ChangeEventHandler<
		HTMLTextAreaElement | HTMLInputElement
	> = ({ target: { value } }) => {
		const option = options.find((e) => e.value === value);

		if (option) {
			onChange(option);
		}
	};
	return (
		<TextField
			data-testid={dataTestId}
			id={id}
			className={className}
			required={required}
			disabled={disabled}
			size={size}
			select
			fullWidth
			label={isMobile ? undefined : label}
			value={
				isMobile && !selectedOption
					? NativeLabelOptionValue
					: selectedOption
			}
			onChange={handleChange}
			variant={variant}
			error={Boolean(error)}
			helperText={typeof error === 'string' ? error : null}
			sx={[...(Array.isArray(sx) ? sx : [sx])]}
			slotProps={{
				select: {
					native: isMobile,
					label,
					sx:
						isMobile && !selectedOption
							? (theme) => ({
									color: theme.palette.text.secondary,
								})
							: {},
				},
			}}>
			{(isMobile && !selectedOption ? [NativeLabelOption] : []).concat(
				options.map(({ text, value }: SelectOption) => (
					<Option
						key={`${id}-${value}`}
						selected={selectedOption === value}
						value={value}>
						{text}
					</Option>
				))
			)}
		</TextField>
	);
};
